/**
 * HISIP-014: Dashboard Settings Page
 * 
 * Restructured settings with tabs for Providers & Models, and Billing
 * Profiles are now managed in separate pages
 */

import React from 'react';
import { CreditCard, Trash2, ExternalLink, Activity, Palette, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import BillingTab from '@/components/settings/BillingTab';
import AccountDangerZone from '@/components/settings/AccountDangerZone';
import { ThemeSelector } from '@/components/theme/ThemeSelector';
import { useServerAuth } from '@/hooks/useAuth-cookie';

const SettingsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const { isAdmin } = useServerAuth();

  // Use project-1 as default if no projectId in params
  const currentProjectId = projectId || 'project-1';

  // No tabs — single page with Billing then Account
  
  // Get Temporal Web URL from environment (only shown to admins)
  const temporalWebUrl = import.meta.env.VITE_TEMPORAL_WEB_URL;
  const temporalNamespace = import.meta.env.VITE_TEMPORAL_NAMESPACE || 'default';
  
  const handleTemporalWebClick = () => {
    if (temporalWebUrl) {
      window.open(temporalWebUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="void-foundation min-h-screen">
      <AtmosphericBackground />
      
      <div className="relative z-10 p-6 space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-light text-gradient-cyan tracking-wider mb-2">
            SYSTEM SETTINGS
          </h1>
          <p className="text-white/50 font-mono text-sm">APPEARANCE, BILLING AND ACCOUNT</p>
        </div>

        {/* Admin Operations Section - Only visible to admin users */}
        {isAdmin && temporalWebUrl && (
          <HolographicCard glow={'blue'} intensity="subtle">
            <CardHeader 
              title={'ADMIN OPERATIONS'}
              subtitle={'Pipeline observability and operational controls'}
              icon={<Activity className="w-5 h-5" />}
            />
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm">
                  <div>
                    <h3 className="text-white font-medium text-sm">Temporal Web</h3>
                    <p className="text-white/60 text-xs mt-1">
                      Monitor workflows and activities — {temporalNamespace}
                    </p>
                  </div>
                  <button
                    onClick={handleTemporalWebClick}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 hover:border-blue-500/50 transition-all duration-200"
                  >
                    <span className="text-sm">Open Web UI</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </HolographicCard>
        )}

        {/* Appearance Section */}
        <HolographicCard glow={'purple'} intensity="subtle">
          <CardHeader 
            title={'APPEARANCE'}
            subtitle={'Theme preferences and visual settings'}
            icon={<Palette className="w-5 h-5" />}
          />
          <CardContent>
            <div className="space-y-6">
              <ThemeSelector showFeedback={true} />
              
              {/* Dynamic Theme Demo Link */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between p-4 rounded-lg border border-purple-500/20 bg-purple-500/5 backdrop-blur-sm">
                  <div>
                    <h3 className="text-white font-medium text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Dynamic Theme System
                    </h3>
                    <p className="text-white/60 text-xs mt-1">
                      Test dynamic theme loading from backend API and custom JSON
                    </p>
                  </div>
                  <Link
                    to="/settings/themes"
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600/20 border border-purple-500/30 text-purple-300 hover:bg-purple-600/30 hover:border-purple-500/50 transition-all duration-200"
                  >
                    <span className="text-sm">Open Demo</span>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </HolographicCard>

        {/* Billing Section */}
        <HolographicCard glow={'cyan'} intensity="subtle">
          <CardHeader 
            title={'BILLING & CREDITS'}
            subtitle={'Manage subscription, credits, invoices'}
            icon={<CreditCard className="w-5 h-5" />}
          />
          <CardContent>
            <BillingTab />
          </CardContent>
        </HolographicCard>

        {/* Account Section */}
        <HolographicCard glow={'error'} intensity="subtle">
          <CardHeader 
            title={'ACCOUNT'}
            subtitle={'Delete account and related data'}
            icon={<Trash2 className="w-5 h-5" />}
          />
          <CardContent>
            <AccountDangerZone projectId={currentProjectId} />
          </CardContent>
        </HolographicCard>

        {/* Profiles live on dedicated pages; no quick links here */}
      </div>
    </div>
  );
};

export default SettingsPage;